// packages/ui-kit/src/primitives/SectionHeading.tsx
// C3F v18 — reference: design/system/components/core/SectionHeading.{jsx,prompt.md}
// Section header: mono eyebrow in accent (the ONE recurrent use of full purple
// as text), title, lede, actions on the right.
import { createElement, type ReactNode } from "react";

type Level = 1 | 2 | 3 | 4;

interface Props {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  /** DS name. */
  actions?: ReactNode;
  /** @deprecated pre-v18 alias of `actions`. */
  action?: ReactNode;
  level?: Level;
  className?: string;
}

// DS: level ≤2 speaks px-h2 (bold), 3 px-h3 (semibold); 4 kept for existing
// call sites on the h4 step. The h2/h1 clamps live in the text-h* utilities.
const levelCls: Record<Level, string> = {
  1: "text-h2 font-bold",
  2: "text-h2 font-bold",
  3: "text-h3 font-semibold",
  4: "text-h4 font-semibold",
};

export function SectionHeading({
  eyebrow,
  title,
  description,
  actions,
  action,
  level = 2,
  className = "",
}: Props) {
  const rightSide = actions ?? action;
  return (
    <div className={`flex flex-wrap items-end justify-between gap-6 ${className}`}>
      {/* Left column is a grid with min-w-0: aligned by textAlign, never by
          shrinking the box (build.md: a column sized by its text wraps badly). */}
      <div className="grid min-w-0 gap-2">
        {eyebrow && <div className="text-eyebrow text-accent-text">{eyebrow}</div>}
        {createElement("h" + level, { className: `m-0 ${levelCls[level]}` }, title)}
        {description && <p className="max-w-[58ch] text-lede text-fg-muted">{description}</p>}
      </div>
      {rightSide && <div className="flex flex-shrink-0 items-center gap-2.5">{rightSide}</div>}
    </div>
  );
}
